import { Col, Row } from "antd";
import React from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { Booking } from "../../../../../../../types/bookings";
import { SelectOption } from "../../../../../../../types/select";
import { AppButton } from "../../../../../../atoms/Button";
import { DateField, ReactSelectField } from "../../../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../../atoms/FormElement/InputNumberField";
import { getInputPlaceholderMessage, getValidationProps } from "../../../../../../libs/form";
import { convertToDayjs, DecimalUtils } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import { no_of_installments } from "../../../utils";

export type ServiceChargeTypeValue = "fixed" | "areaSqft" | "percentage";

export const serviceChargeTypeOptions: SelectOption<ServiceChargeTypeValue>[] = [
  { label: "Fixed", value: "fixed" },
  { label: "Per Sq.Ft", value: "areaSqft" },
  { label: "Percentage of rent", value: "percentage" },
];

type ServiceChargeFieldsProps = {
  planIndex: number;
  isPendingGenerateScheduleBreakdown?: boolean;
  onGenerateBreakdown: () => Promise<void>;
};

export const ServiceChargeFields = (props: ServiceChargeFieldsProps) => {
  const { planIndex, onGenerateBreakdown, isPendingGenerateScheduleBreakdown } = props;

  const { propertyDetails, isPaymentPlanApprovalSent } = useBookingSelector();

  const { control, formState, setValue, getValues } = useFormContext();
  const errors = formState?.errors?.plans?.[planIndex]?.serviceCharge;

  const { bookingDetails } = useSelector((state: any) => state?.newBooking);
  const { leaseTerms } = (bookingDetails ?? {}) as Booking;

  const baseFieldName = `plans[${planIndex}].serviceCharge`;

  const selectedType: SelectOption<ServiceChargeTypeValue> = useWatch({ control, name: `${baseFieldName}.rentType` });
  const isPerSqFt = selectedType?.value === "areaSqft";
  const isPercentage = selectedType?.value === "percentage";

  return (
    <div style={{ margin: "1.5rem 0 0" }}>
      <Row gutter={10}>
        <Col span={5}>
          <ReactSelectField
            label="Service Charge Type"
            fieldname={`${baseFieldName}.rentType`}
            control={control}
            iProps={{
              placeholder: getInputPlaceholderMessage("service charge type"),
              isDisabled: isPaymentPlanApprovalSent,
            }}
            onChange={() => {
              setValue(`${baseFieldName}.chargeValue`, null);
              setValue(`${baseFieldName}.annualRent`, null);
            }}
            selectOption={serviceChargeTypeOptions}
            {...getValidationProps({ fieldName: "rentType", errors, isRequired: true })}
          />
        </Col>

        {isPerSqFt && (
          <>
            <Col span={5}>
              <InputNumberField
                label="Unit Area (Sq.Ft)"
                fieldname={`${baseFieldName}.unitAreaInSqft`}
                control={control}
                defaultValue={
                  getValues(`plans[${planIndex}].unitAreaInSqft`) ??
                  (propertyDetails?.areaSqft ? Number(propertyDetails?.areaSqft) : 1)
                }
                inputNumberProps={{ precision: 2 }}
                disabled
              />
            </Col>
            <Col span={5}>
              <InputNumberField
                label="Charge Value"
                fieldname={`${baseFieldName}.chargeValue`}
                control={control}
                disabled={isPaymentPlanApprovalSent}
                onChange={(value) => {
                  const area = getValues(`${baseFieldName}.unitAreaInSqft`) ?? 0;
                  if (!area) return;
                  setValue(
                    `${baseFieldName}.annualRent`,
                    String(DecimalUtils.toNumber(DecimalUtils.multiply(value ?? 0, area))),
                  );
                }}
                inputNumberProps={{
                  placeholder: getInputPlaceholderMessage("charge value per sq.ft"),
                  min: 0,
                }}
                format="currency"
                {...getValidationProps({ fieldName: "chargeValue", errors, isRequired: true })}
              />
            </Col>
          </>
        )}

        {isPercentage && (
          <Col span={5}>
            <InputNumberField
              label="Percentage (%)"
              fieldname={`${baseFieldName}.chargeValue`}
              control={control}
              disabled={isPaymentPlanApprovalSent}
              onChange={(value) => {
                const planAnnualRent = getValues(`plans[${planIndex}].annualRent`) ?? 0;
                if (!planAnnualRent) return;
                const serviceCharge = DecimalUtils.toNumber(
                  DecimalUtils.multiply(planAnnualRent, DecimalUtils.multiply(value ?? 0, 0.01)),
                );
                setValue(`${baseFieldName}.annualRent`, String(serviceCharge));
              }}
              inputNumberProps={{ placeholder: getInputPlaceholderMessage("percentage"), min: 0, max: 100 }}
              {...getValidationProps({ fieldName: "chargeValue", errors, isRequired: true })}
            />
          </Col>
        )}

        <Col span={5}>
          <InputNumberField
            label="Service Charge"
            fieldname={`${baseFieldName}.annualRent`}
            control={control}
            disabled={isPaymentPlanApprovalSent || isPerSqFt || isPercentage}
            inputNumberProps={{ placeholder: getInputPlaceholderMessage("service charge"), min: 0 }}
            format="currency"
            {...getValidationProps({ fieldName: "annualRent", errors, isRequired: true })}
          />
        </Col>
      </Row>

      <Row gutter={10}>
        <Col span={5}>
          <ReactSelectField
            label="No. of Installments"
            fieldname={`${baseFieldName}.installments`}
            control={control}
            iProps={{
              placeholder: getInputPlaceholderMessage("no. of payments"),
              isDisabled: isPaymentPlanApprovalSent,
            }}
            selectOption={no_of_installments}
            {...getValidationProps({ fieldName: "installments", errors, isRequired: true })}
          />
        </Col>

        {planIndex < 1 && (
          <Col span={5}>
            <DateField
              label="Rent Commencement"
              fieldname={`${baseFieldName}.rentCommencementDate`}
              initValue={convertToDayjs(leaseTerms?.commencementDate)}
              disabled
            />
          </Col>
        )}

        {!isPaymentPlanApprovalSent && (
          <Col style={{ flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <AppButton
              variant="subtle"
              style={{ marginBottom: "2rem" }}
              onClick={onGenerateBreakdown}
              loading={isPendingGenerateScheduleBreakdown}
            >
              Generate Breakdown
            </AppButton>
          </Col>
        )}
      </Row>
    </div>
  );
};
